/**
 * Maps per-element issue texts to specific "tip to fix" strings.
 * Covers all 24 mismatch variants across rules.
 */
export function getIssueDisplayName(i18n, issueText, fallbackName = '') {
	const __ = typeof i18n === 'function' ? i18n : (text) => text;
	const normalizedIssueText = String(issueText || '').trim();

	if (normalizedIssueText && normalizedIssueText !== 'image-intersection') {
		return normalizedIssueText;
	}

	if (String(fallbackName || '').trim()) {
		return String(fallbackName || '').trim();
	}

	return __( 'Rule', 'a11yfix' );
}

export function getIssueVariantTip(i18n, ruleKey, issueText) {
	const __ = typeof i18n === 'function' ? i18n : (text) => text;
	const text = String(issueText || '').trim();

	if (ruleKey === 'imageAlt') {
		return __( 'Add concise alt text that describes the image purpose.', 'a11yfix' );
	}

	if (ruleKey === 'linkTitle') {
		return __( 'Ensure each link has descriptive visible text.', 'a11yfix' );
	}

	if (ruleKey === 'headingOrder') {
		if (text.startsWith('No semantic headings')) {
			return __( 'Add at least one H1 heading to establish page structure.', 'a11yfix' );
		}

		if (text.startsWith('Out-of-order: first heading is not H1')) {
			return __( 'Start page content with an H1 heading; it defines the main topic.', 'a11yfix' );
		}

		if (text.startsWith('Skipped level')) {
			return __( 'Avoid skipping heading levels (e.g., H2 then H4). Use consecutive heading levels.', 'a11yfix' );
		}

		if (text.startsWith('Out-of-order sequence near')) {
			return __( 'Ensure heading levels follow a logical nested order without gaps.', 'a11yfix' );
		}

		if (text.startsWith('Multiple H1')) {
			return __( 'Use only one H1 per page to represent the primary topic.', 'a11yfix' );
		}

		if (text.startsWith('Presentational headings found')) {
			return __( 'Use semantic heading elements (H1–H6) instead of styled text for visual headings.', 'a11yfix' );
		}

		return __( 'Keep heading levels in a logical hierarchy without skipping levels.', 'a11yfix' );
	}

	if (ruleKey === 'formLabels') {
		return __( 'Add a label element linked via for/id, wrap the input in a label, or set aria-label/aria-labelledby.', 'a11yfix' );
	}

	if (ruleKey === 'accessibleNamePreferredSource') {
		return __( 'Update aria-label to match the visible label, or remove aria-label and rely on the visible label text.', 'a11yfix' );
	}

	if (ruleKey === 'ariaLabelledbyIntegrity') {
		if (text.startsWith('aria-labelledby is present but does not contain valid IDs')) {
			return __( 'Set aria-labelledby to a space-separated list of valid element IDs that exist in the DOM.', 'a11yfix' );
		}

		if (text.startsWith('aria-labelledby references missing IDs')) {
			return __( 'Ensure every ID listed in aria-labelledby exists in the DOM. Add the missing element or correct the ID reference.', 'a11yfix' );
		}

		if (text.startsWith('aria-labelledby references do not provide readable text')) {
			return __( 'The referenced element exists but has no text content. Add visible text to the referenced element.', 'a11yfix' );
		}

		if (text.startsWith('aria-labelledby references hidden nodes')) {
			return __( 'Remove aria-hidden from referenced label elements, or use aria-label with the label text directly.', 'a11yfix' );
		}

		return __( 'Ensure aria-labelledby IDs exist and resolve to readable text.', 'a11yfix' );
	}

	if (ruleKey === 'ariaHiddenInteractive') {
		if (text.includes('has aria-hidden="true"')) {
			return __( 'Remove aria-hidden="true" from the interactive element itself.', 'a11yfix' );
		}

		if (text.includes('inside an aria-hidden')) {
			return __( 'Remove aria-hidden="true" from the ancestor container, or move the interactive element outside the hidden region.', 'a11yfix' );
		}

		return __( 'Do not hide interactive content with aria-hidden="true".', 'a11yfix' );
	}

	if (ruleKey === 'keyboardFocusable') {
		if (text.startsWith('Invalid tabindex')) {
			return __( 'Set tabindex to an integer: 0 to include in natural order, or -1 to remove from tab order programmatically.', 'a11yfix' );
		}

		if (text.startsWith('Interactive element removed from tab order')) {
			return __( 'Remove tabindex="-1" or manage focus programmatically so keyboard users can still reach this element.', 'a11yfix' );
		}

		if (text.startsWith('Interactive element is not keyboard-focusable')) {
			return __( 'Add tabindex="0" or use a natively focusable element type such as button or a[href].', 'a11yfix' );
		}

		if (text.startsWith('Avoid positive tabindex')) {
			return __( 'Replace tabindex > 0 with tabindex="0" and rely on DOM order for a logical focus sequence.', 'a11yfix' );
		}

		if (text.startsWith('Element uses CSS order')) {
			return __( 'Avoid using CSS order property on focusable elements; keep DOM order consistent with visual order.', 'a11yfix' );
		}

		if (text.startsWith('Custom interactive role')) {
			return __( 'Add keydown handler for Enter and Space keys to match native button/link keyboard behavior.', 'a11yfix' );
		}

		if (text.startsWith('Focus styles appear to be removed')) {
			return __( 'Restore :focus-visible styles or provide a clearly visible custom focus indicator.', 'a11yfix' );
		}

		if (text.startsWith('Dialog has no focusable elements')) {
			return __( 'Add at least one focusable element (button, link, or input) inside the dialog.', 'a11yfix' );
		}

		if (text.startsWith('Dialog should set aria-modal')) {
			return __( 'Add aria-modal="true" to the dialog element when it traps focus as a modal overlay.', 'a11yfix' );
		}

		if (text.startsWith('Dialog may not receive initial focus')) {
			return __( 'Add autofocus to the first actionable element inside the dialog, or call .focus() on the container when opening.', 'a11yfix' );
		}

		return __( 'Ensure interactive elements are keyboard-focusable and usable.', 'a11yfix' );
	}

	if (ruleKey === 'lowColorContrast') {
		if (text.includes('image-intersection')) {
			return __( 'Increase contrast between text and background to meet WCAG ratios (4.5:1 for normal text, 3:1 for large text). Note: this text intersects with an image/complex background, so the contrast estimate may not be fully accurate.', 'a11yfix' );
		}

		return __( 'Increase contrast between text and background to meet WCAG ratios (4.5:1 for normal text, 3:1 for large text).', 'a11yfix' );
	}

	return '';
}
